import {
  BlockStack,
  Checkbox,
  LegacyStack,
  RadioButton,
  Text,
  TextContainer,
} from '@shopify/polaris'
import React, { useCallback } from 'react'

import { MemoedSelect, MemoedTextField, range } from '../common/helpers'
import { HelpLabel } from '../common/HelpIcon'
import { useShopCurrency } from '../common/Price'
import {
  formatWorkflowActionPrice,
  isLegacyWorkflowActionPrice,
  normalizeWorkflowActionPrice,
  WorkflowAction,
  workflowActionLabels,
  WorkflowAddDiscountDataType,
} from '../common/workflow-models'
import { WorkflowActionPriceFields } from './WorkflowActionPriceFields'

const paymentNumberOptions = range(1, 100).map((i) => ({
  label: `${i}`,
  value: `${i}`,
}))

export const AddDiscountAction = ({
  params,
  onChange,
  index,
  discountNameError,
  adjustmentValueError,
}: {
  params: WorkflowAddDiscountDataType
  onChange: (action: WorkflowAction, index: number) => any
  index: number
  discountNameError: string | null
  adjustmentValueError: string | null
}) => {
  const [discountName, discountRecurringLimit, discountType, adjustmentValue, appliesOnEachItem] = [
    params[0],
    params[1],
    params[2],
    params[3],
    params[4] ?? 1,
  ]

  const currencyCode = useShopCurrency()

  const handleUpdate = useCallback(
    (params: WorkflowAddDiscountDataType) => {
      onChange(['add-discount', params], index)
    },
    [index, onChange]
  )

  const isPercentage = discountType === 'percentage'

  return (
    <LegacyStack vertical>
      <Text variant="headingSm" as="h2">
        {workflowActionLabels['add-discount']}
      </Text>

      <MemoedTextField
        label="Discount name"
        value={discountName}
        onChange={(val: string) =>
          handleUpdate([
            val,
            discountRecurringLimit,
            discountType,
            adjustmentValue,
            appliesOnEachItem ?? 1,
          ])
        }
        autoComplete="off"
        error={discountNameError ?? undefined}
      />
      <LegacyStack vertical spacing="extraTight">
        <RadioButton
          label="Percentage"
          checked={isPercentage}
          name={`discountType-${index}`}
          onChange={() =>
            handleUpdate([
              discountName,
              discountRecurringLimit,
              'percentage',
              '',
              appliesOnEachItem ?? 1,
            ])
          }
        />
        <RadioButton
          label="Fixed Amount"
          checked={!isPercentage}
          name={`discountType-${index}`}
          onChange={() =>
            handleUpdate([
              discountName,
              discountRecurringLimit,
              'fixedAmount',
              '',
              appliesOnEachItem ?? 1,
            ])
          }
        />
      </LegacyStack>
      {isPercentage && (
        <MemoedTextField
          label="Adjustment value"
          type="number"
          autoComplete="off"
          suffix="%"
          value={isLegacyWorkflowActionPrice(adjustmentValue) ? adjustmentValue : ''}
          min="0"
          max="100"
          onChange={(val: string) =>
            handleUpdate([
              discountName,
              discountRecurringLimit,
              discountType,
              val,
              appliesOnEachItem ?? 1,
            ])
          }
          error={adjustmentValueError ?? undefined}
        />
      )}
      {!isPercentage && (
        <LegacyStack vertical>
          <BlockStack gap="200">
            <HelpLabel tooltip='Set the amount per currency. Subscriptions are billed in their own currency, so this discount is applied only when an amount for the subscription&apos;s currency is listed. Choose "Any currency" to use one amount for every currency that is not listed.'>
              Adjustment value
            </HelpLabel>
            <WorkflowActionPriceFields
              prices={normalizeWorkflowActionPrice(adjustmentValue, currencyCode)}
              onChange={(amounts) =>
                handleUpdate([
                  discountName,
                  discountRecurringLimit,
                  discountType,
                  amounts,
                  appliesOnEachItem ?? 1,
                ])
              }
              error={adjustmentValueError}
              index={index}
              bannerText="This discount will only be applied to subscriptions billed in the currencies listed here."
            />
          </BlockStack>
          <Checkbox
            name={`appliesOnEachItem-${index}`}
            label={
              <LegacyStack vertical spacing="extraTight">
                <TextContainer>Only apply discount once per order</TextContainer>
                <Text as="span" tone="subdued">
                  If not selected,{' '}
                  {formatWorkflowActionPrice(adjustmentValue, currencyCode) ?? 'the amount'} will be
                  taken off each item in an order.
                </Text>
              </LegacyStack>
            }
            checked={appliesOnEachItem === 0}
            onChange={(val: boolean) =>
              handleUpdate([
                discountName,
                discountRecurringLimit,
                discountType,
                adjustmentValue,
                val ? 0 : 1,
              ])
            }
          />
        </LegacyStack>
      )}
      <LegacyStack vertical spacing="extraTight">
        <RadioButton
          label="Limit discount to this order only"
          checked={discountRecurringLimit === '1'}
          name={`discountRecurringLimit-${index}`}
          onChange={() =>
            handleUpdate([discountName, '1', discountType, adjustmentValue, appliesOnEachItem ?? 1])
          }
        />
        <RadioButton
          label={
            <LegacyStack vertical spacing="extraTight">
              <TextContainer>Limit discount to multiple orders</TextContainer>
              {discountRecurringLimit !== null && parseInt(discountRecurringLimit) > 1 && (
                <MemoedSelect
                  label=""
                  labelHidden
                  value={discountRecurringLimit}
                  onChange={(val: string) =>
                    handleUpdate([
                      discountName,
                      val,
                      discountType,
                      adjustmentValue,
                      appliesOnEachItem ?? 1,
                    ])
                  }
                  options={paymentNumberOptions}
                />
              )}
            </LegacyStack>
          }
          checked={discountRecurringLimit != null && parseInt(discountRecurringLimit) > 1}
          name={`discountRecurringLimit-${index}`}
          onChange={() =>
            handleUpdate([discountName, '2', discountType, adjustmentValue, appliesOnEachItem ?? 1])
          }
        />
        <RadioButton
          label="Discount applies to this and all future orders"
          checked={discountRecurringLimit === null}
          name={`discountRecurringLimit-${index}`}
          onChange={() =>
            handleUpdate([
              discountName,
              null,
              discountType,
              adjustmentValue,
              appliesOnEachItem ?? 1,
            ])
          }
        />
      </LegacyStack>
    </LegacyStack>
  )
}
